import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ToastContainer from '../components/ToastContainer';
import OrderCard, { OrderDetail } from '../components/OrderCard';
import { apiFetch } from '../utils/apiClient';
import { useVendor } from '../contexts/VendorContext';

interface Props {
  adminView?: boolean;
}

type Filter = 'all' | 'pending' | 'confirmed' | 'closed';

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

const Orders: React.FC<Props> = ({ adminView }) => {
  const { t } = useTranslation();
  const { refreshPendingOrders } = useVendor();
  const [orders, setOrders] = useState<OrderDetail[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = (message: string, type: ToastData['type']) =>
    setToasts((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, message, type }]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (filter === 'pending') params.set('confirmation', 'pending');
      if (filter === 'confirmed') params.set('confirmation', 'confirmed');
      const response = await apiFetch(`/api/orders/?${params.toString()}`);
      if (!response.ok) throw new Error(t('common.genericError'));
      const data = await response.json();
      let results: OrderDetail[] = data.results || [];
      if (filter === 'closed') {
        results = results.filter((o) => ['declined', 'expired'].includes(o.confirmation_status) || o.status !== 'open');
      }
      // The list endpoint is lightweight; fetch details for cards
      const detailed = await Promise.all(
        results.map(async (o) => {
          const r = await apiFetch(`/api/orders/${o.id}/`);
          return r.ok ? ((await r.json()) as OrderDetail) : o;
        }),
      );
      setOrders(detailed);
      setCount(data.count || 0);
      setError('');
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
    } finally {
      setLoading(false);
    }
  }, [page, filter, t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const respond = async (order: OrderDetail, action: 'confirm' | 'decline', reason?: string) => {
    const response = await apiFetch(`/api/orders/${order.id}/${action}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || '' }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      addToast(data.error || t('common.genericError'), 'error');
      fetchOrders();
      return;
    }
    setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
    addToast(action === 'confirm' ? t('orders.confirmedMsg') : t('orders.declinedMsg'), 'success');
    refreshPendingOrders();
  };

  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: t('orders.filterAll') },
    { key: 'pending', label: t('orders.filterPending') },
    { key: 'confirmed', label: t('orders.filterConfirmed') },
    { key: 'closed', label: t('orders.filterClosed') },
  ];

  const totalPages = Math.max(1, Math.ceil(count / 20));

  return (
    <Layout>
      <ToastContainer toasts={toasts} onRemove={(id) => setToasts((p) => p.filter((x) => x.id !== id))} />
      <div className="w-full max-w-4xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          {adminView ? `${t('admin.title')} · ${t('orders.title')}` : t('orders.title')}
        </h1>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap min-h-[40px] ${
                filter === f.key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && <div className="rounded-md bg-red-50 p-4 mb-4 text-sm text-red-800">{error}</div>}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center text-gray-600">{t('orders.noOrders')}</div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} showVendor={adminView} onRespond={respond} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-4 text-sm text-gray-600">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-2 rounded-md border bg-white disabled:opacity-40 min-h-[40px]">
              {t('common.previous')}
            </button>
            <span>{t('common.page', { page, total: totalPages })}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-2 rounded-md border bg-white disabled:opacity-40 min-h-[40px]">
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Orders;
